import React, { useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, navigate } from 'raviger';
import { Button, Card, Col, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Marker, Tooltip as MapTooltip } from "react-leaflet";
import Moment from 'react-moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faExclamationCircle, faSearch, faPhone, faReply } from '@fortawesome/free-solid-svg-icons';
import { faQuestionCircle as faQuestionCircleDuo } from '@fortawesome/pro-duotone-svg-icons';
import { faHomeAlt as faHomeAltReg } from '@fortawesome/pro-regular-svg-icons';
import { faCircleBolt, faHomeAlt, faDoNotEnter, faLocationCrosshairs, faLocationQuestion } from '@fortawesome/pro-solid-svg-icons';
import Map, { AddressLookup, countMatches , BoundsUpdater, prettyText, createClosedMarkerIcon, createReportedMarkerIcon, createReportedEvacMarkerIcon, createReportedSIPMarkerIcon, createSIPMarkerIcon, createUTLMarkerIcon, createOperationsMarkerIcon, createReturnRequestedMarkerIcon } from "../components/Map";
import Header from "../components/Header";
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import { capitalize } from '../utils/formatString';

function MapLegendControl({setShowAddressModal}) {
  return (
    <div className='leaflet-control float-end map-legend mt-2 me-2'>
      <OverlayTrigger
        key={"address-finder"}
        placement="top"
        overlay={
          <Tooltip id={`tooltip-address-finder`}>
            Search for an address to zoom the map to.
          </Tooltip>
        }
      >
        <Button onClick={() => setShowAddressModal(true)}>
          <FontAwesomeIcon icon={faSearch} />
        </Button>
      </OverlayTrigger>
    </div>
  )
}

function Hotline({ incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);
  const { state } = useContext(AuthContext);

  const mapRef = useRef(null);

  const [data, setData] = useState({service_requests: [], isFetching: true, bounds:null});
  const [mapState, setMapState] = useState({});
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [statusOptions, setStatusOptions] = useState("all");
  const [initialBounds, setInitialBounds] = useState(null);
  const [species, setSpecies] = useState([]);
  const [showLegendModal, setShowLegendModal] = useState(false);
  const handleCloseLegendModal = () => setShowLegendModal(false);
  const [reportedMarkerIcon, setReportedMarkerIcon] = useState(createReportedMarkerIcon(13));
  const [reportedEvacMarkerIcon, setReportedEvacMarkerIcon] = useState(createReportedEvacMarkerIcon(13));
  const [reportedSIPMarkerIcon, setReportedSIPMarkerIcon] = useState(createReportedSIPMarkerIcon(13));
  const [SIPMarkerIcon, setSIPMarkerIcon] = useState(createSIPMarkerIcon(13));
  const [UTLMarkerIcon, setUTLMarkerIcon] = useState(createUTLMarkerIcon(13));
  const [returnRequestedMarkerIcon, setReturnRequestedMarkerIcon] = useState(createReturnRequestedMarkerIcon(13));
  const [operationsMarkerIcon, setOperationsMarkerIcon] = useState(createOperationsMarkerIcon(13));
  const [closedMarkerIcon, setClosedMarkerIcon] = useState(createClosedMarkerIcon(13));

  const priorityText = {1:'Urgent', 2:'High', 3:'Medium', 4:'Low', 5:'Lowest'};

  const handleClose = () => {setShowAddressModal(false);}

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchSpecies = () => {
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: capitalize(result.name)});
          });
          setSpecies(response.data);
          fetchServiceRequests(response.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    const fetchServiceRequests = async (species_data) => {
      setData({service_requests: [], isFetching: true, bounds:null});

      // Fetch ServiceRequest data.
      let service_requests = [];
      if (!unmounted) {
        const map_dict = {};
        const bounds = [];
        const dupe_bounds = new Set();
        let nextUrl = '/hotline/api/servicerequests/?page=1&page_size=50&incident=' + incident + '&organization=' + organization;
        do {
          const response = await axios.get(nextUrl, {
            params: {
              landingmap: true
            },
            cancelToken: source.token,
          })
          .catch(error => {
            // setData({service_requests: [], isFetching: false, bounds:null});
            if (error.response) {
              setShowSystemError(true);
            }
          });

          if (response) {
            for (const service_request of response.data.results) {
              const matches = countMatches(service_request.animals, species_data)[0];
              map_dict[service_request.id] = {matches:matches, latitude:service_request.latitude, longitude:service_request.longitude};
              const key = `${service_request.latitude},${service_request.longitude}`;
              if (!dupe_bounds.has(key)) {
                dupe_bounds.add(key);
                bounds.push([service_request.latitude, service_request.longitude]);
              }
            }
            service_requests.push(...response.data.results);
            nextUrl = response.data.next;
            if (nextUrl) {
              nextUrl = '/hotline/' + response.data.next.split('/hotline/')[1];
            }
          }
          else {
            nextUrl = null;
          }
        } while(nextUrl != null)

        setMapState(map_dict);
        // Use the Incident bounds if we don't have any SRs.
        if (service_requests.length === 0) {
          bounds.push([parseFloat(state.incident.coordinates[0])+.04,parseFloat(state.incident.coordinates[1])-.04])
          bounds.push([parseFloat(state.incident.coordinates[0])-.04,parseFloat(state.incident.coordinates[1])+.04])
        }
        if (bounds.length > 0) {
          setData({service_requests: service_requests, isFetching: false, bounds:bounds});
          setInitialBounds(bounds);
        }
        else {
          setData({service_requests: [], isFetching: false, bounds:null});
        }
      }
    };

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident]);

  return (
    <>
    <Header>Hotline
    {/* {isSubscribed ?
    <OverlayTrigger
      key={"unsubscribe"}
      placement="bottom"
      overlay={
        <Tooltip id={`tooltip-unsubscribe`}>
          You are currently subscribed. Click to unsubscribe to stop receiving email notifications when Service Requests are created for this incident.
        </Tooltip>
      }
    >
      <Button className="ms-1" onClick={() => subscribe()} style={{marginTop:"-8px", width:"44px"}}>
        <FontAwesomeIcon size="lg" icon={faBellSlash} transform={'left-3'} />
      </Button>
    </OverlayTrigger>
    :
    <OverlayTrigger
      key={"subscribe"}
      placement="bottom"
      overlay={
        <Tooltip id={`tooltip-subscribe`}>
          You are currently unsubscribed. Click to subscribe to receive email notifications when Service Requests are created for this incident.
        </Tooltip>
      }
    >
      <Button className="ms-1" onClick={() => subscribe()} style={{marginTop:"-8px"}}>
        <FontAwesomeIcon size="lg" icon={faBell} />
      </Button>
    </OverlayTrigger>
    } */}
    </Header>
    <Row className="ms-0 me-0 ps-0 pe-0 mb-0">
      <Col xs={5} className="ps-0 pe-0">
        <Link href={"/" + organization + "/" + incident + "/hotline/workflow/owner"} style={{textDecoration:"none"}}>
          <Button className="rounded border btn-block w-100" style={{height:"100px", fontSize:"20px"}}><FontAwesomeIcon icon={faPhone} className="me-1 fa-move-up" inverse />OWNER CALLING</Button>
        </Link>
      </Col>
      <Col xs={5} className="ps-0 pe-0">
        <Link href={"/" + organization + "/" + incident + "/hotline/workflow/reporter"} style={{textDecoration:"none"}}>
          <Button className="rounded border btn-block w-100" style={{height:"100px", fontSize:"20px"}}><FontAwesomeIcon icon={faPhone} className="me-1 fa-move-up" inverse />NON-OWNER CALLING</Button>
        </Link>
      </Col>
      {/* <Col xs={4} className="ps-0 pe-0">
        <Link href={"/" + organization + "/" + incident + "/hotline/workflow/first_responder"} style={{textDecoration:"none"}}>
          <Button className="rounded border btn-block" style={{height:"100px", fontSize:"20px"}}><FontAwesomeIcon icon={faPhone} className="me-1 fa-move-up" inverse />FIRST RESPONDER CALLING</Button>
        </Link>
      </Col> */}
    </Row>
    <Row xs={12} className="ms-0 me-0 ps-0 pe-0" style={{marginBottom:"-1px"}}>
      <Col xs={10} className="border rounded ps-0 pe-0">
        {!data.isFetching && data.bounds.length > 0 ?
          <Map ref={mapRef} zoom={12} bounds={data.bounds.length > 1 ? data.bounds : null} center={data.bounds.length === 1 ? [data.bounds[0][0], data.bounds[0][1]] : null}  className="landing-leaflet-container" setReportedMarkerIcon={setReportedMarkerIcon} setSIPMarkerIcon={setSIPMarkerIcon} setUTLMarkerIcon={setUTLMarkerIcon} setReturnRequestedMarkerIcon={setReturnRequestedMarkerIcon} setReportedEvacMarkerIcon={setReportedEvacMarkerIcon} setReportedSIPMarkerIcon={setReportedSIPMarkerIcon} setOperationsMarkerIcon={setOperationsMarkerIcon} setClosedMarkerIcon={setClosedMarkerIcon}>
            <MapLegendControl setShowAddressModal={setShowAddressModal} />
            <BoundsUpdater bounds={data.bounds} />
            {data.service_requests.filter(service_request => (service_request.status === statusOptions || statusOptions === "all")).map(service_request => (
              <Marker
                key={service_request.id}
                position={[service_request.latitude, service_request.longitude]}
                icon={service_request.reported_animals > 0 ? reportedMarkerIcon : service_request.reported_evac > 0 ? reportedEvacMarkerIcon : service_request.reported_sheltered_in_place > 0 ? reportedSIPMarkerIcon : service_request.sheltered_in_place > 0 ? SIPMarkerIcon : service_request.unable_to_locate > 0 ? UTLMarkerIcon : service_request.animals.length === 0 ? operationsMarkerIcon : service_request.transport_requested > 0 ? returnRequestedMarkerIcon : closedMarkerIcon}
                eventHandlers={{
                  click() {
                    navigate('/' + organization + "/" + incident + "/hotline/servicerequest/" + service_request.id_for_incident);
                  },
                }}
              >
                <MapTooltip autoPan={false}>
                  <span>
                    SR#{service_request.id_for_incident}: {service_request.full_address}
                    <br/>
                    {mapState[service_request.id] ?
                      <span>
                        {Object.keys(mapState[service_request.id].matches).map((key,i) => (
                          <span key={key} style={{textTransform:"capitalize"}}>
                            {i > 0 && ", "}{prettyText(key.split(',')[0], mapState[service_request.id].matches[key], species)}
                          </span>
                        ))}
                      </span>
                    :""}
                    {service_request.priority ? <div style={{ color: service_request.priority === 1 ? "#ff4c4c" : "black"}}>Priority: {priorityText[service_request.priority]}</div>: ""}
                    {service_request.followup_date ? <div>Followup Date: <Moment format="L">{service_request.followup_date}</Moment></div> : ""}
                    <div>
                      {service_request.aco_required ? <img width={16} height={16} src="/static/images/badge-sheriff.png" alt="Special Handling Required" className="me-1" /> : ""}
                      {service_request.injured ? <img width={16} height={16} src="/static/images/band-aid-solid.png" alt="Injured" className="me-1" /> : ""}
                      {service_request.accessible ? <img width={16} height={16} src="/static/images/car-solid.png" alt="Accessible" className="me-1" /> : <img width={16} height={16} src="/static/images/car-ban-solid.png" alt="Not Acessible" className="me-1" />}
                      {service_request.turn_around ? <img width={16} height={16} src="/static/images/trailer-solid.png" alt="Turn Around" /> : <img width={16} height={16} src="/static/images/trailer-ban-solid.png" alt="No Turn Around" className="me-1" />}
                    </div>
                  </span>
                </MapTooltip>
              </Marker>
            ))}
          </Map>
        :
          <Card className="text-center" style={{height:"450px", marginRight:"-1px", paddingTop:"225px", fontSize:"30px"}}>Fetching Service Requests...</Card>
        }
      </Col>
      {data.service_requests.length > 0 ?
      <Col xs={2} className="ms-0 me-0 ps-0 pe-0 border rounded">
        <Button variant={statusOptions === "all" ? "primary" : "secondary"} className="border" onClick={() => setStatusOptions("all")} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>All ({data.service_requests.length})</Button>
        <Button variant={statusOptions === "open" ? "primary" : "secondary"} className="border" onClick={() => setStatusOptions("open")} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>Open ({data.service_requests.filter(sr => sr.status === "open").length})</Button>
        <Button variant={statusOptions === "assigned" ? "primary" : "secondary"} className="border" onClick={() => setStatusOptions("assigned")} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>Assigned ({data.service_requests.filter(sr => sr.status === "assigned").length})</Button>
        <Button variant={statusOptions === "closed" ? "primary" : "secondary"} className="border" onClick={() => setStatusOptions("closed")} style={{maxHeight:"36px", width:"100%", marginTop:"-1px"}}>Closed ({data.service_requests.filter(sr => sr.status === "closed").length})</Button>
      </Col>
      :
      <Col xs={2} className="ms-0 me-0 ps-0 pe-0 border rounded">
        <Button className="border" style={{maxHeight:"36px", width:"100%", marginTop:"-1px", backgroundColor:"rgb(128, 128, 128)", cursor:"default"}}>None</Button>
      </Col>
      }
    </Row>
    <Row className="ms-0 me-0 border rounded" style={{maxHeight:"38px"}}>
      <h5 className="card-header d-flex align-items-center" style={{height:"36px", width:"100%", backgroundColor:"#808080"}}>
        Service Requests
        <OverlayTrigger
          key={"map-icon-legend"}
          placement="top"
          overlay={
            <Tooltip id={`tooltip-map-icon-legend`}>
              Click to see the map legend.
            </Tooltip>
          }
        >
            <FontAwesomeIcon icon={faLocationQuestion} onClick={() => setShowLegendModal(true)} className="ms-1" style={{cursor:"pointer"}} />
        </OverlayTrigger>
        {/* <span className="fa-layers me-1">
          <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" />
          <FontAwesomeIcon icon={faExclamationCircle} className="icon-border fa-move-down" color="#ff4c4c" />
        </span>
        Reported
        <span style={{paddingRight:"15px", paddingLeft:"15px"}}>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" />
            <FontAwesomeIcon icon={faCircleBolt} className="icon-border fa-move-down" color="#ff4c4c" />
          </span>
          Reported (Evac Req)
        </span>
        <span style={{paddingRight:"15px"}}>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="#ff4c4c" transform={'grow-2'} />
            <FontAwesomeIcon icon={faHomeAlt} className="fa-move-down" style={{color:"white"}} transform={'shrink-4 left-1'} inverse />
            <FontAwesomeIcon icon={faHomeAltReg} className="fa-move-down" style={{color:"#444"}} transform={'shrink-3 left-1'} inverse />
          </span>
          Reported (SIP Req)
        </span>
        <span style={{paddingRight:"5px", paddingLeft:"5px"}}>
          <span className="fa-layers" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="#f5ee0f" transform={'grow-2'} />
            <FontAwesomeIcon icon={faHomeAlt} className="fa-move-down" style={{color:"white"}} transform={'shrink-3 left-1'} inverse />
            <FontAwesomeIcon icon={faHomeAltReg} className="fa-move-down" style={{color:"#444"}} transform={'shrink-3 left-1'} inverse />
          </span>
        SIP
        </span>
        <span style={{paddingRight:"15px", paddingLeft:"15px"}}>
          <span className="fa-layers ms-1 me-1">
            <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" />
            <FontAwesomeIcon icon={faQuestionCircleDuo} className="icon-border fa-move-down" style={{"--fa-primary-color":'white', "--fa-secondary-color":'#5f5fff', "--fa-secondary-opacity": 1}}  />
          </span>
          UTL
        </span>
        <span style={{paddingRight:"15px"}}>
          <span className="fa-layers ms-1 me-1">
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="grey" />
            <FontAwesomeIcon icon={faLocationCrosshairs} className="icon-border fa-move-down" color="white" />
          </span>
          Support
        </span>
        <span className="fa-layers ms-1 me-1">
          <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" />
          <FontAwesomeIcon icon={faDoNotEnter} className="icon-border fa-move-down" color="#af7051" />
        </span>
          Closed */}
      </h5>
    </Row>
    <Modal show={showLegendModal} onHide={handleCloseLegendModal} dialogClassName="small-modal">
      <Modal.Header closeButton>
        <Modal.Title>Map Legend</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fa-layers ms-1" style={{marginRight:"6px"}}>
          <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" transform={'grow-2'} />
          <FontAwesomeIcon icon={faExclamationCircle} className="icon-border fa-move-down" color="#ff4c4c" transform={'grow-2'} />
        </div>
        Reported
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" transform={'grow-2'} />
            <FontAwesomeIcon icon={faCircleBolt} className="icon-border fa-move-down" color="#ff4c4c" transform={'grow-2'} />
          </span>
          Reported (Evac Requested)
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="#ff4c4c" transform={'grow-2'} />
            <FontAwesomeIcon icon={faHomeAlt} className="fa-move-down" style={{color:"white"}} transform={'shrink-4 left-1'} inverse />
            <FontAwesomeIcon icon={faHomeAltReg} className="fa-move-down" style={{color:"#444"}} transform={'shrink-3 left-1'} inverse />
          </span>
          Reported (SIP Requested)
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="#f5ee0f" transform={'grow-2'} />
            <FontAwesomeIcon icon={faHomeAlt} className="fa-move-down" style={{color:"white"}} transform={'shrink-3 left-1'} inverse />
            <FontAwesomeIcon icon={faHomeAltReg} className="fa-move-down" style={{color:"#444"}} transform={'shrink-3 left-1'} inverse />
          </span>
        Sheltered in Place (SIP)
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" transform={'grow-2'} />
            <FontAwesomeIcon icon={faQuestionCircleDuo} className="icon-border fa-move-down" transform={'grow-2'} style={{"--fa-primary-color":'white', "--fa-secondary-color":'#5f5fff', "--fa-secondary-opacity": 1}}  />
          </span>
          Unable to Locate (UTL)
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="#af7051" transform={'grow-2'} />
            <FontAwesomeIcon icon={faReply} className="icon-border fa-move-down" color="white" transform={'shrink-4'} />
          </span>
          Transport Requested
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="icon-border fa-move-down" color="grey" transform={'grow-2'} />
            <FontAwesomeIcon icon={faLocationCrosshairs} className="icon-border fa-move-down" color="white" />
          </span>
          Is Support Request
        </div>
        <div>
          <span className="fa-layers ms-1" style={{marginRight:"6px"}}>
            <FontAwesomeIcon icon={faCircle} className="fa-move-down" color="white" transform={'grow-2'} />
            <FontAwesomeIcon icon={faDoNotEnter} className="icon-border fa-move-down" color="#1e1d1d" transform={'grow-2'} />
          </span>
          Closed
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseLegendModal}>Close</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showAddressModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Address Finder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AddressLookup style={{width: '100%'}} className={"form-control"} setData={setData} initialBounds={initialBounds} incident={incident} handleClose={handleClose} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  </>
  )
}

export default Hotline
